import React, { useState, useEffect, useMemo } from "react"
import { Drawer, Form, Input, Select, Button, Space, message, Spin } from "antd"
import { doc, getDoc, writeBatch, Timestamp, collection } from "firebase/firestore"

import { ExclamationOutlined } from "@ant-design/icons"
import { useCollection } from "../../hooks/useCollection"
import { useAuthContext } from "../../hooks/useAuthContext"
import { db } from "../../firebase/config"

const NewTicketDrawer = ({
  // The specific item being passed in (e.g., an order or invoice object)
  contextItem = {},
  // The type of item, as a string (e.g., "Order" or "Invoice")
  contextType = "",
  // The list of items to show in the dropdown (e.g., all selectable orders)
  selectableItems = [],
  // A boolean to show a loading spinner on the dropdown
  itemsLoading = false,
  // A callback function from the parent to handle specific Firestore updates
  onTicketCreate,
  // Props for the trigger button to make it reusable
  buttonProps = { text: "Create New Ticket", type: "primary" },
  initialFormValues = {},
}) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()

  const ticketType = Form.useWatch("ticketType", form)

  const { data: usersData, loading: usersLoading } = useCollection("users")
  const [settings, setSettings] = useState({ ticketType: [], issueType: [] })

  const { user } = useAuthContext()

  // Fetches general settings - this is reusable and stays.
  useEffect(() => {
    const fetchSettings = async () => {
      const snap = await getDoc(doc(db, "settings", "ticketSettings"))
      if (snap.exists()) {
        setSettings(snap.data())
      }
    }
    fetchSettings()
  }, [])

  // Combines the passed-in contextItem with the list of selectable items, avoiding duplicates.
  const itemOptions = useMemo(() => {
    const allItems = new Map()
    if (contextItem.id) {
      allItems.set(contextItem.id, contextItem)
    }
    selectableItems.forEach((item) => {
      if (!allItems.has(item.id)) {
        allItems.set(item.id, item)
      }
    })
    return Array.from(allItems.values())
  }, [contextItem, selectableItems])

  // Autofills the form if a contextItem is provided when the drawer opens.
  useEffect(() => {
    if (open && contextItem.id && contextType) {
      const updates = {
        ticketType: contextType,
        contextId: contextItem.id,
        ...initialFormValues,
      }
      form.setFieldsValue(updates)
    }
  }, [open, contextItem, contextType, form, initialFormValues])

  // Clears the contextId field if the ticketType is changed.
  useEffect(() => {
    if (ticketType && ticketType !== contextType) {
      form.setFieldsValue({ contextId: undefined })
    }
  }, [ticketType, contextType, form])

  if (!user) return null

  const openDrawer = () => {
    form.resetFields()
    setOpen(true)
  }
  const closeDrawer = () => setOpen(false)

  const handleFinish = async (values) => {
    setLoading(true)
    try {
      const contextId = values.contextId
      let contextData = {} // Holds specific data like orderNumber or invoiceNumber

      const finalItem = itemOptions.find((o) => o.id === contextId)
      if (finalItem) {
        // Build a context object based on the ticket type
        if (values.ticketType === "Order") {
          contextData = {
            orderId: finalItem.id,
            orderNumber: finalItem.orderNumber || "",
          }
        } else if (values.ticketType === "Invoice") {
          contextData = {
            invoiceId: finalItem.id,
            invoiceNumber: finalItem.docPrefix + String(finalItem.docNumber).padStart(4, "0") || "",
          }
        }
      }

      // --- Generic Ticket Creation Logic (preserved) ---
      const seqRef = doc(db, "sequence", "tickets")
      const seqSnap = await getDoc(seqRef)
      const lastNum = seqSnap.data()?.lastTicketNumber || 0
      const nextNum = lastNum + 1
      const ticketId = `#TI${nextNum}`
      const timestamp = Timestamp.now()

      const userMap = new Map((usersData || []).map((u) => [u.id, u.email]))
      const selectedIds = values.assignedTo || []
      const selectedEmails = selectedIds.map((id) => userMap.get(id)).filter(Boolean)
      const assignedToIds = [...new Set([user.uid, ...selectedIds])]
      const assignedToEmails = [...new Set([user.email, ...selectedEmails])]

      const firstMessage = {
        id: `${timestamp.toMillis()}-${user.uid}`,
        text: values.description,
        senderId: user.uid,
        senderEmail: user.email,
        timestamp: timestamp,
      }

      const ticketData = {
        ticketId,
        ...contextData, // Spread in { orderId, orderNumber } or { invoiceId, invoiceNumber }
        stage: values.ticketType,
        issueType: values.issueType,
        description: values.description,
        createdBy: user.email,
        createById: user.uid,
        createdAt: timestamp,
        status: "open",
        assignedTo: assignedToEmails,
        assignedToId: assignedToIds,
        messages: [firstMessage],
        history: [{ action: "created", by: user.email, byId: user.uid, timestamp, details: {} }],
      }

      const batch = writeBatch(db)
      const ticketsCol = collection(db, "tickets")
      batch.set(doc(ticketsCol, ticketId), ticketData)
      batch.update(seqRef, { lastTicketNumber: nextNum })

      // --- Callback to Parent ---
      // This lets the parent component add its own specific updates to the batch
      if (onTicketCreate && typeof onTicketCreate === "function") {
        onTicketCreate(batch, { contextId, assignedToIds })
      }

      await batch.commit()
      message.success(`Ticket ${ticketId} created successfully!`)
      form.resetFields()
      closeDrawer()
    } catch (err) {
      console.error(err)
      message.error("Failed to create ticket. Please check the console for details.")
    } finally {
      setLoading(false)
    }
  }

  // Renders the correct dropdown (Order, Invoice, etc.) based on the selected Ticket Type
  const renderDynamicField = () => {
    if (!ticketType) return null

    const fieldConfig = {
      Order: {
        label: "Select Order",
        placeholder: "Search and select an order",
        renderOption: (item) => `${item.orderNumber} - ${item.client?.client || "N/A"}`,
      },
      Invoice: {
        label: "Select Invoice",
        placeholder: "Search and select an invoice",
        renderOption: (item) =>
          `${item.docPrefix}${String(item.docNumber).padStart(4, "0")} - ${
            item.client?.client || "N/A"
          }`,
      },
    }

    const config = fieldConfig[ticketType]
    if (!config) return null

    return (
      <Form.Item name="contextId" label={config.label} rules={[{ required: true }]}>
        <Select
          showSearch
          disabled={contextItem.id ? true : false}
          placeholder={config.placeholder}
          loading={itemsLoading}
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }>
          {itemOptions.map((item) => (
            <Select.Option key={item.id} value={item.id} label={config.renderOption(item)}>
              {config.renderOption(item)}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    )
  }

  return (
    <>
      <Button
        type={buttonProps.type}
        icon={contextItem.id ? <ExclamationOutlined /> : undefined}
        size={contextItem.id ? "small" : "middle"}
        danger={contextItem.id ? true : false}
        onClick={openDrawer}>
        {contextItem.id ? "Report" : buttonProps.text}
      </Button>
      <Drawer title="New Ticket" width={400} onClose={closeDrawer} open={open}>
        <Spin spinning={usersLoading}>
          <Form layout="vertical" form={form} onFinish={handleFinish}>
            <Form.Item name="ticketType" label="Ticket Type" rules={[{ required: true }]}>
              <Select disabled={contextItem.id ? true : false} placeholder="Select ticket type">
                {(settings.ticketType || []).map((t) => (
                  <Select.Option key={t} value={t}>
                    {t}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            {/* This function now renders the correct field */}
            {renderDynamicField()}

            <Form.Item name="issueType" label="Issue Type" rules={[{ required: true }]}>
              <Select placeholder="Select issue type">
                {(settings.issueType || []).map((t) => (
                  <Select.Option key={t} value={t}>
                    {t}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, max: 200 }]}>
              <Input.TextArea rows={3} placeholder="Short description of the issue..." />
            </Form.Item>

            <Form.Item name="assignedTo" label="Assign to">
              <Select mode="multiple" placeholder="Select users" optionFilterProp="label">
                {(usersData || []).map((u) => (
                  <Select.Option key={u.id} value={u.id} label={u.email}>
                    {u.email}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item>
              <Space>
                <Button onClick={closeDrawer}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Create Ticket
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Drawer>
    </>
  )
}

export default NewTicketDrawer
